import { useEffect } from 'react';
import { useQueryState } from 'nuqs';

/** localStorage key that persists the "reveal hidden auth providers" opt-in. */
export const SHOW_HIDDEN_PROVIDERS_STORAGE_KEY = 'reflex:show-hidden-auth-providers';

/** Read the persisted opt-in from localStorage. Safe to call outside React. */
export function readShowHiddenProviders(): boolean {
  try {
    return localStorage.getItem(SHOW_HIDDEN_PROVIDERS_STORAGE_KEY) === 'true';
  } catch {
    // localStorage can throw in private-mode / sandboxed contexts.
    return false;
  }
}

function writeShowHiddenProviders(enabled: boolean): void {
  try {
    if (enabled) localStorage.setItem(SHOW_HIDDEN_PROVIDERS_STORAGE_KEY, 'true');
    else localStorage.removeItem(SHOW_HIDDEN_PROVIDERS_STORAGE_KEY);
  } catch {
    // Best-effort: ignore storage failures.
  }
}

/**
 * Whether hidden auth providers should be revealed on the login / registration
 * screens.
 *
 * The opt-in is sticky: visiting any auth page once with `?showHidden=true`
 * persists the choice to localStorage (and `?showHidden=false` clears it), so
 * the flag survives future visits without the query param. The param is
 * stripped from the URL after it is consumed.
 */
export function useShowHiddenProviders(): boolean {
  const [param, setParam] = useQueryState('showHidden');

  // Derive the effective value during render: when the param is present it is
  // the authoritative choice for this visit; otherwise fall back to the
  // persisted opt-in. The effect below only handles the side effects
  // (persisting the choice and stripping the param from the URL), so we never
  // call setState from inside it.
  const enabled = param == null ? readShowHiddenProviders() : param === 'true';

  useEffect(() => {
    if (param == null) return;
    writeShowHiddenProviders(param === 'true');
    void setParam(null, { history: 'replace' });
  }, [param, setParam]);

  return enabled;
}
